/* ==========================================================================
   Sacche Dhaage — Infinite scroll + endless product feed + back to top
   --------------------------------------------------------------------------
   No app, no subscription, no external requests. Plain JS.

   1. Collection pages : auto-loads the next page of products as the shopper
                         scrolls, keeping filters and sorting intact.
   2. Product pages    : an endless "Keep exploring" feed under the product.
   3. Every page       : a floating "back to top" button.

   Degrades gracefully - if JavaScript fails, Dawn's normal numbered
   pagination is still there and still works.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- config */

  // Templates that get collection-style infinite scroll.
  // Add 'search' to this list to switch it on for search results too.
  var SCROLL_TEMPLATES = ['collection'];

  // How far ahead of the viewport to start fetching the next page.
  var PRELOAD_PX = 1200;
  var FEED_PRELOAD_PX = 600;

  // Scroll distance before the back-to-top button appears.
  var BTT_THRESHOLD = 500;

  var STRINGS = {
    loading: 'Loading more…',
    end: 'You have reached the end',
    error: 'Could not load more products.',
    retry: 'Try again'
  };

  /* --------------------------------------------------------------- helpers */

  function templateName() {
    var main = document.querySelector('main[data-template]');
    return main ? main.getAttribute('data-template') : '';
  }

  function pageOf(href) {
    try {
      return parseInt(new URL(href, window.location.origin).searchParams.get('page') || '0', 10) || 0;
    } catch (e) {
      return 0;
    }
  }

  function fetchDocument(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  // Dawn hides grid items until its reveal-on-scroll observer picks them up.
  // Items injected later never get observed, so strip the animation hooks.
  function cleanItem(node) {
    node.classList.remove('scroll-trigger');
    node.classList.remove('scroll-trigger--offscreen');
    node.classList.remove('animate--slide-in');
    node.removeAttribute('data-cascade');
    node.style.removeProperty('--animation-order');
    return node;
  }

  // True when a grid item links to exactly this product handle.
  function isProduct(item, handle) {
    var links = item.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var path = links[i].getAttribute('href').split('?')[0].split('#')[0].replace(/\/$/, '');
      if (path.indexOf('/products/') !== -1 && path.split('/products/')[1] === handle) return true;
    }
    return false;
  }

  function hasPageAfter(scope, page) {
    var wrapper = scope.querySelector('.pagination-wrapper');
    if (!wrapper) return false;
    var links = wrapper.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      if (pageOf(links[i].getAttribute('href')) > page) return true;
    }
    return false;
  }

  function buildStatus() {
    var wrap = document.createElement('div');
    wrap.className = 'sd-scroll-status';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'polite');
    wrap.hidden = true;
    return wrap;
  }

  // Dawn's base.css hides every empty div, so give the sentinel a child.
  function buildSentinel() {
    var el = document.createElement('div');
    el.className = 'sd-scroll-sentinel';
    el.setAttribute('aria-hidden', 'true');
    el.appendChild(document.createElement('span'));
    return el;
  }

  function showSpinner(status) {
    status.hidden = false;
    status.innerHTML =
      '<span class="sd-scroll-spinner" aria-hidden="true"></span><span>' + STRINGS.loading + '</span>';
  }

  function showEnd(status) {
    status.hidden = false;
    status.innerHTML = '<span class="sd-scroll-end">' + STRINGS.end + '</span>';
  }

  function showError(status, onRetry) {
    status.hidden = false;
    status.innerHTML = '<span>' + STRINGS.error + '</span>';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'sd-retry-button';
    button.textContent = STRINGS.retry;
    button.addEventListener('click', function () {
      status.hidden = true;
      onRetry();
    });
    status.appendChild(button);
  }

  function hide(status) {
    status.hidden = true;
    status.innerHTML = '';
  }

  // Watches a sentinel with IntersectionObserver plus a throttled scroll
  // fallback, so it still fires if the shopper jumps straight to the bottom
  // or if CSS keeps the observer from seeing it.
  function watchSentinel(sentinel, margin, callback) {
    var ticking = false;
    var stopped = false;
    var observer = null;

    // Reached: the sentinel's top has come within `margin` of the viewport
    // bottom - including when the shopper has already scrolled past it.
    function reached() {
      return sentinel.getBoundingClientRect().top <= window.innerHeight + margin;
    }

    function onScroll() {
      if (ticking || stopped) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        if (!stopped && reached()) callback();
      });
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          if (entries[0].isIntersecting && !stopped) callback();
        },
        { rootMargin: margin + 'px 0px' }
      );
      observer.observe(sentinel);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.requestAnimationFrame(onScroll);

    return {
      poke: onScroll,
      stop: function () {
        stopped = true;
        if (observer) observer.disconnect();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    };
  }

  /* ------------------------------------------- 1. collection page scrolling */

  function initCollectionScroll() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    var grid = container.querySelector('#product-grid');
    if (!grid || grid.tagName !== 'UL') return;

    var wrapper = container.querySelector('.pagination-wrapper');
    if (!wrapper) return;

    var currentPage = parseInt(wrapper.getAttribute('data-page') || '1', 10) || 1;
    if (!hasPageAfter(container, currentPage)) return;

    wrapper.classList.add('sd-pagination-hidden');

    var status = buildStatus();
    var sentinel = buildSentinel();
    var gridParent = grid.parentNode;
    gridParent.appendChild(status);
    gridParent.appendChild(sentinel);

    var hasNext = true;
    var busy = false;
    var stopped = false;
    var watcher = watchSentinel(sentinel, PRELOAD_PX, loadNext);

    function nextUrl() {
      var url = new URL(window.location.href);
      url.searchParams.set('page', String(currentPage + 1));
      return url.toString();
    }

    function finish() {
      busy = false;
      stopped = true;
      watcher.stop();
      showEnd(status);
    }

    function loadNext() {
      if (busy || stopped || !hasNext) return;
      busy = true;
      showSpinner(status);

      fetchDocument(nextUrl())
        .then(function (doc) {
          var newGrid = doc.getElementById('product-grid');
          var items = newGrid ? newGrid.children : [];
          if (!items.length) {
            hasNext = false;
            finish();
            return;
          }

          var fragment = document.createDocumentFragment();
          for (var i = 0; i < items.length; i++) {
            fragment.appendChild(cleanItem(document.importNode(items[i], true)));
          }
          grid.appendChild(fragment);
          currentPage += 1;
          busy = false;

          hasNext = hasPageAfter(doc, currentPage);
          if (hasNext) {
            hide(status);
            watcher.poke();
          } else {
            finish();
          }
        })
        .catch(function () {
          busy = false;
          showError(status, loadNext);
        });
    }

    return function destroy() {
      stopped = true;
      watcher.stop();
      if (status.parentNode) status.parentNode.removeChild(status);
      if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
    };
  }

  // Dawn's filters replace the whole grid container. Re-initialise when they do.
  function watchFacets() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || !('MutationObserver' in window)) return;

    var destroy = initCollectionScroll();
    var timer = null;

    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (typeof destroy === 'function') {
          try {
            destroy();
          } catch (e) {
            /* already detached */
          }
        }
        destroy = initCollectionScroll();
      }, 150);
    }).observe(container, { childList: true });
  }

  /* --------------------------------------------- 2. endless feed on product */

  function initFeed(section) {
    var feed = section.querySelector('[data-sd-feed]');
    if (!feed || feed.hasAttribute('data-sd-ready')) return;
    feed.setAttribute('data-sd-ready', 'true');

    var grid = feed.querySelector('[data-sd-feed-grid]');
    var base = feed.getAttribute('data-sd-feed-url');
    var exclude = feed.getAttribute('data-sd-feed-exclude') || '';
    var maxPages = parseInt(feed.getAttribute('data-sd-feed-max') || '0', 10) || 0;
    if (!grid || !base) return;

    var status = buildStatus();
    var sentinel = buildSentinel();
    feed.appendChild(status);
    feed.appendChild(sentinel);

    var page = 0;
    var busy = false;
    var done = false;
    var added = 0;
    var watcher = watchSentinel(sentinel, FEED_PRELOAD_PX, loadNext);

    function stop(showMessage) {
      done = true;
      watcher.stop();
      if (showMessage && added) showEnd(status);
      else hide(status);
    }

    function loadNext() {
      if (busy || done) return;
      if (maxPages && page >= maxPages) {
        stop(false);
        return;
      }
      busy = true;
      showSpinner(status);

      var url = new URL(base, window.location.origin);
      url.searchParams.set('page', String(page + 1));

      fetchDocument(url.toString())
        .then(function (doc) {
          var newGrid = doc.getElementById('product-grid');
          var items = newGrid ? newGrid.children : [];
          if (!items.length) {
            if (!added && !(window.Shopify && window.Shopify.designMode)) section.hidden = true;
            stop(true);
            return;
          }

          var fragment = document.createDocumentFragment();
          for (var i = 0; i < items.length; i++) {
            if (exclude && isProduct(items[i], exclude)) continue;
            fragment.appendChild(cleanItem(document.importNode(items[i], true)));
            added += 1;
          }
          grid.appendChild(fragment);
          page += 1;
          busy = false;

          if (hasPageAfter(doc, page)) {
            hide(status);
            watcher.poke();
          } else {
            stop(true);
          }
        })
        .catch(function () {
          busy = false;
          showError(status, loadNext);
        });
    }
  }

  function initAllFeeds(root) {
    var sections = (root || document).querySelectorAll('.sd-feed-section');
    for (var i = 0; i < sections.length; i++) initFeed(sections[i]);
  }

  /* -------------------------------------------------- 3. back to top button */

  function initBackToTop() {
    var button = document.getElementById('SDBackToTop');
    if (!button || button.hasAttribute('data-sd-ready')) return;
    button.setAttribute('data-sd-ready', 'true');

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var ticking = false;

    function update() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop;
      if (y > BTT_THRESHOLD) button.classList.add('is-visible');
      else button.classList.remove('is-visible');
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    button.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });

    update();
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    try {
      initBackToTop();
    } catch (e) {
      /* never let this break the page */
    }
    try {
      if (SCROLL_TEMPLATES.indexOf(templateName()) !== -1) watchFacets();
    } catch (e) {
      /* ignore */
    }
    try {
      initAllFeeds(document);
    } catch (e) {
      /* ignore */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor: re-run when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    try {
      initAllFeeds(event.target);
      initBackToTop();
    } catch (e) {
      /* ignore */
    }
  });
})();
